import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './QRCodeGenerator.css'; // We'll create this CSS file

const QRCodeGenerator = () => {
  const [qrCodes, setQrCodes] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const printRef = useRef();

  // Generate 320 unique 6-digit codes
  const generateSixDigitCodes = () => {
    const codes = [];
    const usedCodes = new Set();
    
    while (codes.length < 320) {
      // Generate a random 6-digit number (100000 to 999999)
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Ensure uniqueness
      if (!usedCodes.has(code)) {
        usedCodes.add(code);
        codes.push(code);
      }
    }
    
    return codes;
  };

  // Generate QR codes
  const generateQRCodes = () => {
    setIsGenerating(true);
    
    // Simulate async operation to prevent UI freeze
    setTimeout(() => {
      const sixDigitCodes = generateSixDigitCodes();
      setQrCodes(sixDigitCodes);
      setIsGenerating(false);
    }, 100);
  };

  // Print all QR codes
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Codes - 6 Digit Codes</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px;
              background: white;
            }
            .qr-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 20px;
              max-width: 1200px;
              margin: 0 auto;
            }
            .qr-item {
              text-align: center;
              padding: 15px;
              border: 1px solid #eee;
              border-radius: 8px;
              background: white;
              break-inside: avoid;
            }
            .qr-code {
              margin: 10px auto;
              display: flex;
              justify-content: center;
            }
            .qr-label {
              margin-top: 10px;
              font-size: 16px;
              font-weight: bold;
              color: #333;
              font-family: monospace;
              letter-spacing: 2px;
            }
            .qr-header {
              text-align: center;
              margin-bottom: 30px;
            }
            .qr-header h1 {
              color: #333;
              font-size: 24px;
            }
            .qr-header p {
              color: #666;
              font-size: 16px;
            }
            @media print {
              body { margin: 0; }
              .qr-item { 
                border: none;
                box-shadow: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-header">
            <h1>QR Codes - 6 Digit Codes</h1>
            <p>Total Codes: ${qrCodes.length}</p>
          </div>
          <div class="qr-grid">
            ${qrCodes.map(code => `
              <div class="qr-item">
                <div class="qr-code">
                  ${document.getElementById(`qr-${code}`)?.outerHTML || ''}
                </div>
                <div class="qr-label">${code}</div>
              </div>
            `).join('')}
          </div>
          <script>
            window.onload = () => window.print();
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Download as HTML file
  const handleDownload = () => {
    const qrCodesHTML = qrCodes.map(code => `
      <div style="
        display: inline-block;
        margin: 10px;
        padding: 15px;
        border: 1px solid #ddd;
        border-radius: 8px;
        text-align: center;
        width: 150px;
        page-break-inside: avoid;
      ">
        <div style="margin: 0 auto; width: 128px; height: 128px;">
          ${document.getElementById(`qr-${code}`)?.outerHTML || ''}
        </div>
      </div>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Codes - 6 Digit Codes</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            background: white;
          }
          .container {
            max-width: 1200px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding: 20px;
            background: #f5f5f5;
            border-radius: 8px;
          }
          .qr-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 15px;
          }
          @media print {
            .qr-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>QR Codes - 6 Digit Codes</h1>
            <p>Generated: ${new Date().toLocaleString()}</p>
            <p>Total Codes: ${qrCodes.length}</p>
          </div>
          <div class="qr-grid">
            ${qrCodesHTML}
          </div>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qrcodes-6digit-${new Date().toISOString().slice(0,10)}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download as PDF (requires browser print to PDF)
  const handleDownloadPDF = () => {
    handlePrint(); // User can choose "Save as PDF" from print dialog
  };

  return (
    <div className="qr-generator-container">
      <div className="controls">
        <h1>QR Code Generator - 6 Digit Codes</h1>
        <div className="button-group">
          <button 
            onClick={generateQRCodes} 
            disabled={isGenerating}
            className="btn btn-primary"
          >
            {isGenerating ? 'Generating...' : 'Generate 320 QR Codes'}
          </button>
          
          {qrCodes.length > 0 && (
            <>
              <button onClick={handlePrint} className="btn btn-secondary">
                Print QR Codes
              </button>
              <button onClick={handleDownload} className="btn btn-success">
                Download HTML
              </button>
              <button onClick={handleDownloadPDF} className="btn btn-info">
                Download as PDF
              </button>
            </>
          )}
        </div>
        
        <div className="stats">
          <p>Total QR Codes: {qrCodes.length}/320</p>
        </div>
      </div>

      <div ref={printRef} className="qr-codes-display">
        {qrCodes.length > 0 && (
          <div className="qr-grid">
            {qrCodes.map((code) => (
              <div key={code} className="qr-item">
                <div id={`qr-${code}`} className="qr-code-wrapper">
                  <QRCodeSVG 
                    value={code} 
                    size={128}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                {/* <div className="qr-label">{code}</div> */}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeGenerator;